import { isScreenshotMode } from "@constants/common";
import type { Skill } from "@features/report/helpers/point-normalization";
import { getCompositeScoreAndLevel } from "@features/report/helpers/point-normalization";
import { calculateAccuracy } from "@helpers/common";
import { useAppStore } from "@hooks/useAppStore";
import type { AppTheme } from "@hooks/useAppTheme";
import type { Distribution } from "@memorang/types/src/content";
import type { MixedItemTag } from "@memorang/types/src/tag";
import type { TFunction } from "i18next";
import type { Content } from "types/tag";
import { getProgressColorScale } from "./distribution";
const calculateParentDist = (contents: Content[]) => {
	const masteryLevels = useAppStore.getState().app.masteryLevels!;
	const parentDist = masteryLevels
		.map((item) => item.id)
		.reduce<Record<string, number>>((acc, curr) => {
			acc[curr] = 0;
			return acc;
		}, {});
	const accumulateDist = (dist: Distribution) => {
		for (const key of Object.keys(dist)) {
			const finalKey = key as keyof Distribution;
			parentDist[finalKey] = parentDist[finalKey] || 0;
			parentDist[finalKey] += dist[finalKey];
		}
	};

	for (const content of contents) {
		accumulateDist(content.distribution);
	}

	return parentDist;
};

const getParentMetadata = (contents: Content[]) => {
	const dist = calculateParentDist(contents);

	return {
		distribution: dist,
		isFavorited: false,
		permissions: ["VIEW"],
	};
};

export const getColorScaleAndDescription = ({
	theme,
	tag,
	t,
	includeColorScale = true,
}: {
	theme: AppTheme;
	tag: MixedItemTag;
	t: TFunction;
	includeColorScale?: boolean;
}) => {
	const showPointsBasedProgress =
		useAppStore.getState().reportView?.formative?.showPointsBasedScore;
	const masteryLevels = useAppStore.getState().app.masteryLevels!;
	if (showPointsBasedProgress) {
		const { skillDetails } = getCompositeScoreAndLevel(
			[
				{
					skill: tag.label as Skill,
					numPointsEarned: tag.numPointsEarned || 0,
					numTotalPoints: tag.numTotalPoints || 0,
				},
			],
			t,
		);

		const [firstSkill] = skillDetails;
		const masteryLevel = masteryLevels.find(
			(item) => item.id === firstSkill.level,
		);
		const description = `${t("common.estimatedProficiency")} - ${t(masteryLevel?.alias.title || "")}`;

		const colorScale = [firstSkill.compositeColor, theme.colors.progress.none];

		return {
			colorScale,
			description,
		};
	}

	const score = calculateAccuracy({
		numCorrect: isScreenshotMode
			? Math.floor(Math.random() * (tag.numItems - 8)) + 9
			: tag.numCorrect || 0,
		numTotal: tag.numItems || 0,
	});
	const scoreRanges = useAppStore.getState().app.scoreRanges;
	const colorScale = getProgressColorScale(score, theme, scoreRanges!);
	return {
		colorScale: includeColorScale ? colorScale : undefined,
		description: "",
	};
};
export { getParentMetadata };
